import { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import PillInput from '../../../UISystem/Forms/PillInput';
import ContactTypeInput from '../../../UISystem/Forms/ContactTypeInput';
import { ButtonRounded } from '../../../UISystem/Buttons';
import { COLORS } from '../../../../styles/UISystem';
import { ALL_CONTACT_TYPES } from '../../../../utils/contactTypeConfig';
import { createAutofillDetector } from '../../../../utils/autofillDetection';

// The simplified trio (user ruling + mocks, 2026-08-08): the input IS the
// active type; the chips are the other two. Tap a chip → it becomes the
// field (placeholder + keyboard), the previous type joins the chips.
const SHARE_TYPE_IDS = ['phone', 'instagram', 'email'];

/**
 * AnonShareBackFlow — the sans-chat share-back: TWO screens, one thing per
 * step (D2; mocks anon_3_Add-Name / anon_4_Add-Contact). "Your name" → one
 * pill field → Next; "Phone or social" → one pill field + type chips → Next.
 *
 * Per-screen autofill: the name field carries autoComplete="name" (no
 * auto-submit — names never self-submit); the contact field reuses the
 * shipped autofill burst detector and auto-advances on a settled autofill
 * (same pattern as AnonShareOverlay). D2 record: no combined one-tap
 * AutoFill-Contact ever reliably existed — per-screen single-field autofill
 * gives up nothing.
 *
 * The parent owns persistence: onNameNext fires at screen-1 Next (name
 * announced to the host immediately — user ruling, device pass 1: the host's
 * card names itself while the person types their contact); onComplete fires
 * at screen-2 Next with the full payload (the authoritative contact write —
 * a separate user action, so no same-action race). The parent renders this
 * flow ABOVE the landing gate, so the mid-flow participation flip is safe.
 */
const AnonShareBackFlow = ({ onNameNext, onComplete }) => {
  const [step, setStep] = useState('name');
  const [name, setName] = useState('');
  const [contactType, setContactType] = useState('phone');
  const [contactValue, setContactValue] = useState('');
  const [autoSubmitPending, setAutoSubmitPending] = useState(false);

  const nameInputRef = useRef(null);
  const autofillDetectorRef = useRef(null);

  // Autofocus the name field on mount; arm the contact autofill detector when
  // the contact screen appears (fresh per entry — stale timestamps can't feed it)
  useEffect(() => {
    if (step === 'name') {
      const t = setTimeout(() => nameInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
    autofillDetectorRef.current = createAutofillDetector(Date.now());
    return undefined;
  }, [step]);

  // Chips = the trio minus whichever type is in the field
  const chipTypes = useMemo(
    () => SHARE_TYPE_IDS.filter((id) => id !== contactType).map((id) => ALL_CONTACT_TYPES[id]),
    [contactType]
  );
  const typeConfig = ALL_CONTACT_TYPES[contactType];

  const trimmedName = name.trim();
  const trimmedContact = typeof contactValue === 'string' ? contactValue.trim() : contactValue;
  const canNextName = trimmedName.length > 0;
  const canNextContact = !!trimmedContact;

  const handleNameNext = () => {
    if (!canNextName) return;
    onNameNext(trimmedName);
    setStep('contact');
  };

  const handleContactNext = () => {
    if (!canNextContact) return;
    onComplete({ name: trimmedName, contactType, contactValue: trimmedContact });
  };

  // Contact-field change: record the keystroke gap; a detected autofill burst
  // arms the pending flag (submit happens post-settle in the effect below).
  const handleContactChange = (value) => {
    setContactValue(value);
    const detector = autofillDetectorRef.current;
    if (detector) {
      detector.track();
      if (detector.wasAutofilled()) setAutoSubmitPending(true);
    }
  };

  // Auto-advance after autofill settles (100ms without further changes) —
  // same settle rule as the shipped overlay: submitting inside the onChange
  // tick truncated the value historically.
  useEffect(() => {
    if (!autoSubmitPending) return undefined;
    const t = setTimeout(() => {
      setAutoSubmitPending(false);
      autofillDetectorRef.current?.reset();
      if (trimmedContact) {
        onComplete({ name: trimmedName, contactType, contactValue: trimmedContact });
      }
    }, 100);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmitPending, contactValue]);

  const handleChipSelect = (typeId) => {
    setContactType(typeId);
    setContactValue('');
  };

  const isNameStep = step === 'name';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        backgroundColor: COLORS.WHITE,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {/* Content sits in the upper third — keyboard up, room to act (D2) */}
      <div
        style={{
          width: '100%',
          maxWidth: '340px',
          padding: '0 20px',
          marginTop: 'calc(env(safe-area-inset-top) + 140px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
        }}
      >
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#000000' }}>
          {isNameStep ? 'Your name' : 'Phone or social'}
        </div>

        <div style={{ width: '100%' }}>
          {isNameStep ? (
            <PillInput
              ref={nameInputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameNext();
              }}
              placeholder="Name"
              autoComplete="name"
              autoCapitalize="words"
            />
          ) : (
            // Field per type via the shared input (extracted device pass 1j);
            // autoFocus mirrors the pre-extraction behavior exactly — handle/
            // email fields focused, phone-like fields not
            <ContactTypeInput
              typeConfig={typeConfig}
              value={contactValue}
              onChange={handleContactChange}
              onEnter={handleContactNext}
              autoFocus={typeConfig?.category !== 'phone-like'}
            />
          )}
        </div>

        {/* Type chips — the OTHER two types only (the field is the active
            one); tapping swaps the field over */}
        {!isNameStep && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '10px',
            }}
          >
            {chipTypes.map((t) => (
              <ButtonRounded
                key={t.id}
                text={t.label}
                size="compact"
                variant="secondary"
                onClick={() => handleChipSelect(t.id)}
              />
            ))}
          </div>
        )}

        <ButtonRounded
          text="Next"
          variant="primary"
          size="large"
          disabled={isNameStep ? !canNextName : !canNextContact}
          onClick={isNameStep ? handleNameNext : handleContactNext}
        />
      </div>
    </div>
  );
};

AnonShareBackFlow.propTypes = {
  onNameNext: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default AnonShareBackFlow;
